import { useEffectivePermissions } from "@/features/authorization/hooks/useEffectivePermissions";
import { Link, useLocation } from "react-router-dom";
import { Users } from "lucide-react";

import { useMemberDirectory } from "@/features/members/useMemberDirectory";
import type { MemberResponse } from "@/api/members";
import { PageHero } from "@/components/layout/PageHero";
import { LoadingPage } from "@/components/ui/LoadingPage";
import { QueryErrorState } from "@/components/ui/QueryErrorState";
import { EmptyState } from "@/components/ui/EmptyState";
import { StatusBadge } from "@/components/ui/StatusBadge";
import {
  DataTable,
  DataTablePagination,
  DataTableToolbar,
  PageContainer,
  SearchBar,
  type DataTableColumn,
} from "@/design-system";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { AddMemberDialog } from "@/features/members/components/AddMemberDialog";
import { EditMemberDialog } from "@/features/members/components/EditMemberDialog";

export function MembersPage() {
  const location = useLocation();
  const { churchId } = useAuth();
  const { permissions } = useEffectivePermissions();

  const {
    data,
    isLoading,
    isError,
    isFetching,
    refetch,
    search, activeSearch, onSearchChange, onPrevious, onNext,
  } = useMemberDirectory(churchId);

  const columns: DataTableColumn<MemberResponse>[] = [
    {
      key: "name",
      header: "Member",
      render: (member) => (
        <div>
          <Link
            to={`/members/${member.id}`}
            className="font-medium text-white hover:text-emerald-400"
          >
            {member.preferred_name || member.first_name}{" "}
            {member.last_name}
          </Link>

          <p className="mt-1 text-xs text-slate-500">
            {member.email || "No email on file"}
          </p>
        </div>
      ),
    },
    {
      key: "attendance",
      header: "Attendance recency",
      render: (member) => {
        const days = getDaysSince(
          member.last_attended_at,
        );

        if (days === null) {
          return (
            <span className="text-slate-500">
              No history
            </span>
          );
        }

        if (days <= 14) {
          return (
            <span className="text-emerald-400">
              Active
            </span>
          );
        }

        if (days <= 45) {
          return (
            <span className="text-amber-400">
              Watch
            </span>
          );
        }

        return (
          <span className="text-rose-400">
            At Risk
          </span>
        );
      },
    },
    {
      key: "lastAttended",
      header: "Last Attended",
      render: (member) =>
        member.last_attended_at
          ? new Date(
              member.last_attended_at,
            ).toLocaleDateString()
          : "-",
    },
    {
      key: "status",
      header: "Status",
      render: (member) => (
        <StatusBadge status={member.status} />
      ),
    },
    {
      key: "actions",
      header: "",
      render: (member) => (
        <div className="flex items-center justify-end gap-3">
          <EditMemberDialog member={member} />

          <Link
            to={`/members/${member.id}`}
            className="font-medium text-indigo-400 hover:text-indigo-300"
          >
            View
          </Link>
        </div>
      ),
    },
  ];

  return (
    <PageContainer>
      {location.state?.memberDeleted && <p role="status" className="mb-4 rounded-xl bg-emerald-500/10 p-4 text-emerald-300">Member permanently deleted.</p>}
      <PageHero
        icon={<Users size={30} />}
        title="Members"
        subtitle="Manage member engagement, health, and pastoral follow-up."
        action={<AddMemberDialog />}
      />

      <DataTableToolbar
        search={
          <SearchBar
            value={search}
            placeholder="Search members..."
            onChange={onSearchChange}
          />
        }
        meta={
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <Users size={16} />
            {isFetching
              ? "Refreshing members..."
              : data ? `${data.total} total member(s)` : ""}
          </div>
        }
      />

      {isLoading ? <LoadingPage /> : isError || !data ? (
        <QueryErrorState
          title="Unable to load members"
          description="MinistryIQ could not retrieve the member directory. Check your connection and try again."
          onRetry={() => void refetch()}
        />
      ) : data.total === 0 && !activeSearch ? (
        <EmptyState
          icon={<Users size={24} />}
          title="Build your member directory"
          description="Import your existing member list or add the first person manually. Member data unlocks attendance tracking and engagement intelligence."
          action={<div className="flex flex-wrap justify-center gap-3"><Link to="/settings/data-import" className="rounded-xl bg-indigo-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-400">Import Members</Link><AddMemberDialog /></div>}
        />
      ) : (
        <DataTable
          data={data.items}
          columns={columns.filter(column => column.key === "health" ? permissions.has("INTELLIGENCE_VIEW") && permissions.has("ATTENDANCE_VIEW") : ["attendance", "lastAttended"].includes(column.key) ? permissions.has("ATTENDANCE_VIEW") : true)}
          emptyTitle="No matching members"
          emptyDescription="Try adjusting your search."
        />
      )}

      {data && !isError && <DataTablePagination
        page={data.page}
        canGoNext={
          data.items.length >= data.page_size
        }
        onPrevious={onPrevious}
        onNext={onNext}
      />}
    </PageContainer>
  );
}

function getDaysSince(
  dateValue?: string | null,
) {
  if (!dateValue) {
    return null;
  }

  const date = new Date(dateValue);
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  return Math.floor(
    diff / (1000 * 60 * 60 * 24),
  );
}

